import {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  type ReactNode,
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  safePolygon,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";
import { CoreTooltip } from "@boi/components-core";

type TooltipOptions = {
  initialOpen?: boolean;
  placement?: CoreTooltip.Position;
  offset?: number;
  maxWidth?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function useTooltip({
  initialOpen = false,
  placement = CoreTooltip.DEFAULT_POSITION,
  offset: offsetValue = CoreTooltip.DEFAULT_OFFSET,
  maxWidth,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const isControlled = Boolean(setControlledOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);

  const floatingData = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip({ padding: CoreTooltip.FLIP_PADDING }),
      shift({ padding: CoreTooltip.SHIFT_PADDING }),
      arrow({
        element: arrowRef,
        padding: CoreTooltip.ARROW_PADDING,
      }),
    ],
  });

  const hover = useHover(floatingData.context, {
    handleClose: safePolygon(),
    enabled: controlledOpen === undefined,
  });

  const focus = useFocus(floatingData.context, {
    enabled: controlledOpen === undefined,
  });

  const dismiss = useDismiss(floatingData.context);

  const role = useRole(floatingData.context, {
    role: "tooltip",
  });

  const interactionsData = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      isControlled,
      maxWidth,
      arrowRef,
      floatingData,
      interactionsData,
    }),
    [
      open,
      setOpen,
      isControlled,
      maxWidth,
      arrowRef,
      floatingData,
      interactionsData,
    ]
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (!context) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }

  return context;
};

export type TooltipProps = TooltipOptions & { children: ReactNode };

function Tooltip(props: TooltipProps) {
  const { children, ...options } = props;
  const tooltip = useTooltip(options);

  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
}

export default Tooltip;
